<template>
  <b-modal
    id="create-rental-agreement-modal"
    ref="modal"
    title="Create Rental Agreement"
    size="lg"
    hide-footer
    header-class="custom-modal-header"
    @shown="initHostCanvas"
    @hidden="resetForm"
  >
    <div class="modal-body-container px-2 py-1">
      <div class="intro-banner p-3 rounded mb-4 d-flex align-items-center">
        <div class="banner-icon mr-3">
          <i class="fa fa-file-text-o"></i>
        </div>
        <div>
          <h6 class="font-weight-bold mb-1 text-dark">Rental Agreement Generator</h6>
          <p class="mb-0 text-muted small">
            Generate a binding lease agreement for your tenant. Select a property, set stay parameters, and sign below.
          </p>
        </div>
      </div>

      <form novalidate @submit.prevent="submitAgreement">
        <!-- Property Selection Dropdown -->
        <div class="form-group mb-3">
          <label class="form-label font-weight-bold">
            Select Property <span class="text-danger">*</span>
          </label>
          <select
            v-model="form.selectedPropertyId"
            class="form-control custom-select-style"
            :class="{ 'is-invalid': submitted && !form.selectedPropertyId }"
            @change="onPropertyChange"
          >
            <option value="" disabled>-- Choose a Property --</option>
            <option v-for="prop in availableProperties" :key="prop.id" :value="prop.id">
              {{ prop.displayTitle }} ({{ prop.displayPrice }})
            </option>
          </select>
          <div v-if="submitted && !form.selectedPropertyId" class="invalid-feedback d-block">
            Please select a property from the dropdown.
          </div>
        </div>

        <div class="row">
          <!-- Tenant Name -->
          <div class="col-md-6 form-group mb-3">
            <label class="form-label font-weight-bold">
              Tenant Full Name <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              v-model="form.tenantName"
              class="form-control custom-input"
              :class="{ 'is-invalid': submitted && !form.tenantName.trim() }"
              placeholder="e.g. Sarah Jenkins"
            />
            <div v-if="submitted && !form.tenantName.trim()" class="invalid-feedback d-block">
              Tenant name is required.
            </div>
          </div>

          <!-- Monthly Rent -->
          <div class="col-md-6 form-group mb-3">
            <label class="form-label font-weight-bold">
              Monthly Rent (AED) <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text bg-light font-weight-bold">AED</span>
              </div>
              <input
                type="number"
                step="0.01"
                v-model="form.monthlyRent"
                class="form-control custom-input"
                :class="{ 'is-invalid': submitted && (!form.monthlyRent || form.monthlyRent <= 0) }"
                placeholder="3000.00"
              />
            </div>
            <div v-if="submitted && (!form.monthlyRent || form.monthlyRent <= 0)" class="invalid-feedback d-block">
              Please enter a valid monthly rent amount.
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Start Date -->
          <div class="col-md-6 form-group mb-3">
            <label class="form-label font-weight-bold">
              Lease Start Date <span class="text-danger">*</span>
            </label>
            <input
              type="date"
              v-model="form.startDate"
              class="form-control custom-input"
              :class="{ 'is-invalid': submitted && !form.startDate }"
            />
            <div v-if="submitted && !form.startDate" class="invalid-feedback d-block">
              Lease start date is required.
            </div>
          </div>

          <!-- End Date -->
          <div class="col-md-6 form-group mb-3">
            <label class="form-label font-weight-bold">
              Lease End Date <span class="text-danger">*</span>
            </label>
            <input
              type="date"
              v-model="form.endDate"
              class="form-control custom-input"
              :class="{ 'is-invalid': submitted && (!form.endDate || form.endDate <= form.startDate) }"
            />
            <div v-if="submitted && !form.endDate" class="invalid-feedback d-block">
              Lease end date is required.
            </div>
            <div v-else-if="submitted && form.endDate <= form.startDate" class="invalid-feedback d-block">
              End date must be after start date.
            </div>
          </div>
        </div>

        <div class="row">
          <!-- Security Deposit -->
          <div class="col-md-6 form-group mb-3">
            <label class="form-label font-weight-bold">
              Security Deposit (AED) <span class="text-danger">*</span>
            </label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text bg-light font-weight-bold">AED</span>
              </div>
              <input
                type="number"
                step="0.01"
                v-model="form.securityDeposit"
                class="form-control custom-input"
                :class="{ 'is-invalid': submitted && (form.securityDeposit === '' || form.securityDeposit < 0) }"
                placeholder="3000.00"
              />
            </div>
            <div v-if="submitted && (form.securityDeposit === '' || form.securityDeposit < 0)" class="invalid-feedback d-block">
              Security deposit is required.
            </div>
          </div>

          <!-- Host Name -->
          <div class="col-md-6 form-group mb-3">
            <label class="form-label font-weight-bold">Host / Landlord Name</label>
            <input
              type="text"
              v-model="form.hostName"
              class="form-control custom-input"
              placeholder="e.g. CheapComforts Property Management"
            />
          </div>
        </div>

        <!-- Digital Signature Canvas Pad for Host (Half Length, Double Height) -->
        <div class="row mb-3">
          <div class="col-md-6">
            <div class="d-flex justify-content-between align-items-center mb-1">
              <label class="form-label font-weight-bold mb-0 text-dark">
                <i class="fa fa-pencil-square-o text-primary mr-1"></i> {{ getCreatorRole() === 'host' ? 'Host Digital Signature Pad (Optional)' : 'Tenant Digital Signature Pad (Optional)' }}
              </label>
              <button type="button" class="btn btn-sm btn-link text-muted p-0 font-12 text-decoration-none" @click="clearHostCanvas">
                <i class="fa fa-eraser mr-1"></i> Clear
              </button>
            </div>
            <div class="signature-canvas-wrapper border rounded p-1 bg-light">
              <canvas
                ref="hostSignatureCanvas"
                width="350"
                height="140"
                class="w-100 bg-white border rounded"
                style="height: 140px;"
                @mousedown="startHostDrawing"
                @mousemove="drawHost"
                @mouseup="stopHostDrawing"
                @mouseleave="stopHostDrawing"
                @touchstart.prevent="startHostDrawingTouch"
                @touchmove.prevent="drawHostTouch"
                @touchend.prevent="stopHostDrawing"
              ></canvas>
            </div>
            <small class="text-muted font-11 d-block mt-1">Sign above to attach your official signature.</small>
          </div>
        </div>

        <!-- Dynamic List for Terms & Conditions -->
        <div class="form-group mb-4">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <label class="form-label font-weight-bold mb-0">
              Terms & Conditions
            </label>
            <button type="button" class="btn btn-sm btn-outline-primary" @click="addTermPoint">
              <i class="fa fa-plus-circle mr-1"></i> Add New Term
            </button>
          </div>
          <div class="terms-bullet-list p-3 rounded border bg-light">
            <div
              v-for="(point, idx) in form.termsList"
              :key="idx"
              class="d-flex align-items-center mb-2"
            >
              <span class="bullet-dot mr-2 text-primary font-weight-bold">•</span>
              <input
                type="text"
                v-model="form.termsList[idx]"
                class="form-control custom-input bg-white"
                :placeholder="'Term #' + (idx + 1)"
              />
              <button
                type="button"
                class="btn btn-sm btn-outline-danger ml-2 px-2"
                @click="removeTermPoint(idx)"
                v-if="form.termsList.length > 1"
                title="Remove point"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="d-flex justify-content-end align-items-center pt-3 border-top">
          <button type="button" class="btn btn-outline-secondary px-4 mr-2" @click="hideModal">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary-gradient px-4" :disabled="submitting">
            <span v-if="!submitting"><i class="fa fa-send mr-1"></i> Send Agreement</span>
            <span v-else><i class="fa fa-spinner fa-spin mr-1"></i> Creating...</span>
          </button>
        </div>
      </form>
    </div>
  </b-modal>
</template>

<script>
import API from "../api";

export default {
  name: "CreateRentalAgreementModal",
  props: {
    conversationId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      submitted: false,
      submitting: false,
      isHostDrawing: false,
      hasHostDrawn: false,
      hostCtx: null,
      availableProperties: [
        {
          id: 101,
          displayTitle: "Private Room Apartment in Dubai, United Arab Emirates.",
          displayPrice: "AED 3000 / 1 Month",
          title: "Private Room Apartment in Dubai, United Arab Emirates.",
          price: 3000
        },
        {
          id: 102,
          displayTitle: "Modern & Fully Furnished Apartment in Dubai, United Arab Emirates.",
          displayPrice: "AED 4500 / 1 Month",
          title: "Modern & Fully Furnished Apartment in Dubai, United Arab Emirates.",
          price: 4500
        },
        {
          id: 103,
          displayTitle: "Executive Beachfront Villa in Dubai, United Arab Emirates.",
          displayPrice: "AED 12000 / 1 Month",
          title: "Executive Beachfront Villa in Dubai, United Arab Emirates.",
          price: 12000
        }
      ],
      form: {
        selectedPropertyId: "",
        propertyTitle: "",
        tenantName: "",
        hostName: "CheapComforts Host",
        startDate: "",
        endDate: "",
        monthlyRent: "",
        securityDeposit: "",
        termsList: [
          "Rent is payable in AED on or before the 1st day of each billing period.",
          "Security deposit is refundable upon move-out inspection.",
          "Premises must be kept in clean condition. No unauthorized sub-leasing permitted."
        ]
      }
    };
  },
  methods: {
    getCreatorRole() {
      let myId = null;
      try {
        const u = JSON.parse(localStorage.getItem("user"));
        myId = u ? (u.id || u.user_id || (u.user && u.user.id)) : null;
      } catch (e) {
        console.warn("Failed to parse user from localStorage", e);
      }
      
      const selectedProp = this.availableProperties.find(p => String(p.id) === String(this.form.selectedPropertyId));
      if (selectedProp && selectedProp.user_id && myId) {
        if (String(selectedProp.user_id) === String(myId)) {
          return "host";
        } else {
          return "tenant";
        }
      }

      // If no property selected yet, check if myId owns ANY property in availableProperties
      if (myId && Array.isArray(this.availableProperties) && this.availableProperties.length > 0) {
        const iOwnAProperty = this.availableProperties.some(p => p.user_id && String(p.user_id) === String(myId));
        if (iOwnAProperty) {
          return "host";
        } else {
          return "tenant";
        }
      }

      if (this.form.tenantId && myId && String(this.form.tenantId) === String(myId)) {
        return "tenant";
      }
      return "host";
    },
    updatePartiesFromContext() {
      if (!this.chatContext) return;
      const { myId, myName, secondUserId, secondUserName } = this.chatContext;
      if (!myId || !secondUserId) return;

      const creatorRole = this.getCreatorRole();
      if (creatorRole === 'host') {
        this.form.hostName = myName || "CheapComforts Host";
        this.form.tenantName = secondUserName || "Tenant";
        this.form.tenantId = secondUserId;
      } else {
        this.form.tenantName = myName || "Tenant";
        this.form.tenantId = myId;
        this.form.hostName = secondUserName || "CheapComforts Host";
      }
    },
    show(defaultData = {}) {
      this.submitted = false;
      this.chatContext = defaultData;
      if (defaultData.tenantName) this.form.tenantName = defaultData.tenantName;
      if (defaultData.tenantId) this.form.tenantId = defaultData.tenantId;
      if (defaultData.hostName) this.form.hostName = defaultData.hostName;

      this.updatePartiesFromContext();
      this.loadProperties();
      this.$refs.modal.show();
    },
    initHostCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.hostSignatureCanvas;
        if (canvas) {
          this.hostCtx = canvas.getContext("2d");
          this.hostCtx.strokeStyle = "#000000";
          this.hostCtx.lineWidth = 2.5;
          this.hostCtx.lineCap = "round";
          this.clearHostCanvas();
        }
      });
    },
    clearHostCanvas() {
      const canvas = this.$refs.hostSignatureCanvas;
      if (canvas && this.hostCtx) {
        this.hostCtx.clearRect(0, 0, canvas.width, canvas.height);
        this.hasHostDrawn = false;
      }
    },
    getHostCanvasCoords(e) {
      const canvas = this.$refs.hostSignatureCanvas;
      const rect = canvas.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) * (canvas.width / rect.width),
        y: (e.clientY - rect.top) * (canvas.height / rect.height)
      };
    },
    startHostDrawing(e) {
      this.isHostDrawing = true;
      const coords = this.getHostCanvasCoords(e);
      this.hostCtx.beginPath();
      this.hostCtx.moveTo(coords.x, coords.y);
    },
    drawHost(e) {
      if (!this.isHostDrawing) return;
      const coords = this.getHostCanvasCoords(e);
      this.hostCtx.lineTo(coords.x, coords.y);
      this.hostCtx.stroke();
      this.hasHostDrawn = true;
    },
    startHostDrawingTouch(e) {
      if (e.touches && e.touches[0]) {
        this.startHostDrawing(e.touches[0]);
      }
    },
    drawHostTouch(e) {
      if (e.touches && e.touches[0]) {
        this.drawHost(e.touches[0]);
      }
    },
    stopHostDrawing() {
      this.isHostDrawing = false;
    },
    addTermPoint() {
      this.form.termsList.push("");
    },
    removeTermPoint(idx) {
      if (this.form.termsList.length > 1) {
        this.form.termsList.splice(idx, 1);
      }
    },
    formatCleanTitle(p) {
      const cleanType = (p.property_type || '').replace(/_/g, ' ').trim();
      const cleanAcc = (p.accommodation_type || '').replace(/_/g, ' ').trim();
      if (cleanType || cleanAcc || p.city) {
        const typeStr = [cleanType, cleanAcc].filter(Boolean).join(" ") || "Private Room Apartment";
        const locStr = [p.city, p.country].filter(Boolean).join(", ");
        const fullStr = `${typeStr} in ${locStr || 'Dubai, United Arab Emirates'}.`;
        return fullStr.replace(/_/g, ' ');
      }
      if (p.title && p.title.length < 65) {
        const titleClean = p.title.replace(/_/g, ' ').trim();
        return titleClean.endsWith(".") ? titleClean : titleClean + ".";
      }
      return `Private Room Apartment in ${p.city || 'Dubai'}, ${p.country || 'United Arab Emirates'}.`;
    },
    formatCleanPrice(p) {
      const rent = p.price_per_month || p.price || 3000;
      const unit = p.price_per_month ? '1 Month' : '1 Month';
      return `AED ${rent}/${unit}`;
    },
    loadProperties() {
      API.getAllProperties(
        { page: 1 },
        (data) => {
          if (data && data.data && data.data.data && data.data.data.length > 0) {
            const fetchedProps = data.data.data.map((p) => {
              const displayTitle = this.formatCleanTitle(p);
              const displayPrice = this.formatCleanPrice(p);
              const priceVal = p.price_per_month || p.price || 3000;
              return {
                id: p.id,
                user_id: p.user_id,
                displayTitle: displayTitle,
                displayPrice: displayPrice,
                title: displayTitle,
                price: priceVal
              };
            });
            this.availableProperties = fetchedProps;
            this.updatePartiesFromContext();
          }
        },
        () => {
          // Keep default fallback properties if API fails or returns empty
        }
      );
    },
    onPropertyChange() {
      const selected = this.availableProperties.find((p) => p.id === this.form.selectedPropertyId);
      if (selected) {
        this.form.propertyTitle = selected.title;
        if (!this.form.monthlyRent) {
          this.form.monthlyRent = selected.price;
        }
      }
      this.updatePartiesFromContext();
    },
    hideModal() {
      this.$refs.modal.hide();
    },
    resetForm() {
      this.submitted = false;
      this.submitting = false;
      this.clearHostCanvas();
      this.form = {
        selectedPropertyId: "",
        propertyTitle: "",
        tenantName: "",
        hostName: "CheapComforts Host",
        startDate: "",
        endDate: "",
        monthlyRent: "",
        securityDeposit: "",
        termsList: [
          "Rent is payable in AED on or before the 1st day of each billing period.",
          "Security deposit is refundable upon move-out inspection.",
          "Premises must be kept in clean condition. No unauthorized sub-leasing permitted."
        ]
      };
    },
    submitAgreement() {
      this.submitted = true;

      // Check validation criteria only after clicking submit
      if (
        !this.form.selectedPropertyId ||
        !this.form.tenantName.trim() ||
        !this.form.startDate ||
        !this.form.endDate ||
        this.form.endDate <= this.form.startDate ||
        !this.form.monthlyRent ||
        this.form.monthlyRent <= 0 ||
        this.form.securityDeposit === "" ||
        this.form.securityDeposit < 0
      ) {
        return;
      }

      let sigDataUrl = null;
      const canvas = this.$refs.hostSignatureCanvas;
      if (canvas && this.hasHostDrawn) {
        sigDataUrl = canvas.toDataURL("image/png");
      }

      const isHostCreator = this.getCreatorRole() === "host";

      this.submitting = true;
      const cleanTermsList = this.form.termsList.filter(t => t && t.trim());
      const agreementPayload = {
        id: "agr_" + Date.now(),
        propertyId: this.form.selectedPropertyId,
        property_id: this.form.selectedPropertyId,
        propertyTitle: this.form.propertyTitle,
        property_title: this.form.propertyTitle,
        tenantName: this.form.tenantName,
        tenant_name: this.form.tenantName,
        tenant_id: this.form.tenantId || null,
        hostName: this.form.hostName,
        host_name: this.form.hostName,
        startDate: this.form.startDate,
        start_date: this.form.startDate,
        endDate: this.form.endDate,
        end_date: this.form.endDate,
        monthlyRent: parseFloat(this.form.monthlyRent || 0).toFixed(2),
        monthly_rent: parseFloat(this.form.monthlyRent || 0).toFixed(2),
        securityDeposit: parseFloat(this.form.securityDeposit || 0).toFixed(2),
        security_deposit: parseFloat(this.form.securityDeposit || 0).toFixed(2),
        termsList: cleanTermsList,
        terms_list: cleanTermsList,
        terms: cleanTermsList.map((t, idx) => `${idx + 1}. ${t}`).join('\n'),
        status: "Pending Signature",
        hostSigned: isHostCreator ? !!sigDataUrl : false,
        host_signed: isHostCreator ? !!sigDataUrl : false,
        hostSignatureImg: isHostCreator ? sigDataUrl : null,
        host_signature_img: isHostCreator ? sigDataUrl : null,
        tenantSigned: !isHostCreator ? !!sigDataUrl : false,
        tenant_signed: !isHostCreator ? !!sigDataUrl : false,
        tenantSignatureImg: !isHostCreator ? sigDataUrl : null,
        tenant_signature_img: !isHostCreator ? sigDataUrl : null,
        created_at: new Date().toISOString()
      };

      if (this.conversationId) {
        API.createRentalAgreement(
          this.conversationId,
          agreementPayload,
          (res) => {
          this.submitting = false;
            const backendAgreement = res.agreement || {};
            const fullPayload = {
              ...agreementPayload,
              ...(res.agreement || {}),   // merge ALL backend fields (host_id, tenant_id, etc.)
              id: backendAgreement.id || agreementPayload.id,
              pdf_url: res.pdf_url || API.getRentalAgreementPdfUrl(backendAgreement.id || agreementPayload.id)
            };
            this.$emit("agreement-created", fullPayload);
            this.hideModal();
          },
          (err) => {
            this.submitting = false;
            console.error("Failed to store agreement", err);
            this.$emit("agreement-created", agreementPayload);
            this.hideModal();
          }
        );
      } else {
        setTimeout(() => {
          this.submitting = false;
          this.$emit("agreement-created", agreementPayload);
          this.hideModal();
        }, 400);
      }
    }
  }
};
</script>

<style scoped>
.intro-banner {
  background: linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%);
  border-left: 4px solid #70d3ff;
}
.banner-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: #70d3ff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.bullet-dot {
  font-size: 18px;
}
.signature-canvas-wrapper {
  touch-action: none;
  cursor: crosshair;
}
.custom-select-style,
.custom-input {
  border-radius: 8px;
  border: 1px solid #dcdfe6;
  padding: 10px 14px;
  font-size: 14px;
  transition: all 0.2s ease-in-out;
}
.custom-select-style:focus,
.custom-input:focus {
  border-color: #70d3ff;
  box-shadow: 0 0 0 3px rgba(112, 211, 255, 0.2);
}
.btn-primary-gradient {
  background: linear-gradient(135deg, #70d3ff 0%, #0099ff 100%);
  border: none;
  color: white;
  font-weight: 600;
  border-radius: 8px;
  padding: 10px 24px;
  transition: opacity 0.2s;
}
.btn-primary-gradient:hover {
  opacity: 0.92;
  color: white;
}
</style>
